import React, { useState, useEffect, useRef } from "react";
import styles from "./TextInputFilter.module.css";

export default function TextInputFilter({
  dropdownTitle,
  placeholder = "Type to search...",
  value = "",
  onApply,
  onClear,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const wrapperRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: '110%', left: 0, transform: 'none' });

  // Sync external value with internal state
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (!wrapperRef.current || !open) return;
    
    const rect = wrapperRef.current.getBoundingClientRect();
    const dropdownHeight = 120; // Estimated height for text input + buttons
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const containerRect = wrapperRef.current.parentElement?.getBoundingClientRect();
    
    let newTop = '110%';
    let newLeft = 0;
    let newTransform = 'none';
    
    // Check if dropdown would go below viewport
    if (rect.bottom + dropdownHeight > viewportHeight) {
      if (rect.top > dropdownHeight) {
        // Show above if there's enough space
        newTop = 'auto';
        newTransform = 'translateY(-100%)';
      } else {
        // Show below but limit height if not enough space above
        newTop = '110%';
      }
    }
    
    // Check if this is the last item in the row or if dropdown would overflow
    const isLastInRow = containerRect && (rect.right + 250 >= containerRect.right || rect.right + 250 >= viewportWidth);
    
    if (isLastInRow) {
      // Align to right for last items to prevent overflow
      newLeft = 'auto';
      newTransform = newTransform === 'translateY(-100%)' ? 'translateY(-100%) translateX(-40%)' : 'translateX(-40%)';
    } else {
      // Default left alignment for other items
      newLeft = 0;
      if (newTransform.includes('translateX(-40%)')) {
        newTransform = newTransform.replace('translateX(-40%)', '');
      }
    }
    
    setDropdownPosition({ top: newTop, left: newLeft, transform: newTransform });
  };

  useEffect(() => {
    calculateDropdownPosition();
    window.addEventListener('resize', calculateDropdownPosition);
    return () => window.removeEventListener('resize', calculateDropdownPosition);
  }, [open, calculateDropdownPosition]);

  // Outside click close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApply = () => {
    onApply?.(inputValue);
    setOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    onClear?.();
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleApply();
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={styles.filterWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.filterBtn}
        onClick={() => setOpen((o) => !o)}
      >
        {dropdownTitle}
        {value && (
          <span className={styles.countBadge}>1</span>
        )}
      </button>

      {open && (
        <div 
          className={styles.dropdown}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            transform: dropdownPosition.transform
          }}
        >
          {/* Input */}
          <input
            type="text"
            value={inputValue}
            placeholder={placeholder}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.input}
            autoFocus
          />

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <button
              type="button"
              onClick={handleApply}
              className={styles.applyBtn}
              disabled={!inputValue.trim()}
            >
              Apply
            </button>
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className={styles.clearBtn}
                title="Clear filter"
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
